import { TABLE_TOP_CORNERS } from "../../../v2/components/tableHeaderCorners.js";
// Read-only, date-proportional Gantt for the filtered Projects population.
// Bars are real recorded phase intervals (one batched schedule read for the
// whole population); there is no aggregate per-project bar.
import React, { useEffect, useMemo, useState } from "react";
import { T } from "../../../app/tokens.js";
import { todayUtcMs, buildScheduleRow, buildPortfolioScale } from "./ganttModel.js";
import { ScheduleTimeline } from "./ScheduleTimeline.jsx";
import { getProjectSchedules, SCHEDULE_SAVED_EVENT } from "../../../data/projectScheduleClient.js";

export function ProjectsGantt({ projects, now, range = "24", density = "comfortable" }) {
  const nowKey = now ? now.getTime() : null;
  const today = useMemo(() => todayUtcMs(nowKey != null ? new Date(nowKey) : new Date()), [nowKey]);
  const idKey = useMemo(() => (projects || []).map((p) => p?.id).filter(Boolean).join(","), [projects]);
  const [load, setLoad] = useState({ status: "loading", byId: null, error: null });
  const [token, setToken] = useState(0);

  useEffect(() => {
    const bump = () => setToken((n) => n + 1);
    window.addEventListener(SCHEDULE_SAVED_EVENT, bump);
    return () => window.removeEventListener(SCHEDULE_SAVED_EVENT, bump);
  }, []);

  useEffect(() => {
    let active = true;
    if (!idKey) { setLoad({ status: "ok", byId: new Map(), error: null }); return undefined; }
    setLoad((l) => ({ status: "loading", byId: l.byId, error: null }));
    Promise.resolve().then(() => getProjectSchedules(idKey.split(",")))
      .then((res) => {
        if (!active) return;
        if (!Array.isArray(res?.schedules)) throw new Error("The server returned an invalid schedule list.");
        const m = new Map();
        for (const s of res.schedules) if (s?.projectId) m.set(s.projectId, s);
        setLoad({ status: "ok", byId: m, error: null });
      })
      .catch((error) => { if (active) setLoad({ status: "error", byId: null, error }); });
    return () => { active = false; };
  }, [idKey, token]);

  const byId = load.byId;
  const rows = useMemo(() => (projects || []).map((p) => {
    const r = buildScheduleRow(p, byId?.get(p.id) || null, { portfolio: true });
    const confirmed = load.status === "ok" && byId?.has(p.id);
    const emptyText = !confirmed ? (load.status === "loading" ? "Loading schedule…" : "Schedule unavailable")
      : r.invalid.length ? "Invalid phase dates"
      : r.partial.length ? "Incomplete phase dates"
      : !r.hasPhaseRows ? "No phase schedule"
      : !r.bars.length ? "Phase dates not recorded" : null;
    return { ...r, emptyText, unscheduled: confirmed && !r.hasPhaseRows };
  }).sort((a, b) => Number(a.unscheduled) - Number(b.unscheduled)), [projects, byId, load.status]);
  const scale = useMemo(() => buildPortfolioScale(rows, today, range), [rows, today, range]);

  return (
    <div data-testid="projects-gantt" style={{ border: "1px solid var(--v2-line)", borderRadius: 10, ...TABLE_TOP_CORNERS, background: T.card }}>
      {load.status === "error" && (
        <div role="alert" data-testid="projects-gantt-error" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: T.lightGray, color: T.red, fontSize: T.fsSm, borderBottom: "1px solid var(--v2-hair)" }}>
          Phase schedules could not be loaded: {load.error?.message || "request failed"}
          <button type="button" data-testid="projects-gantt-retry" onClick={() => setToken((n) => n + 1)} style={{ border: `1px solid ${T.border}`, background: T.card, borderRadius: 6, padding: "3px 10px", fontSize: T.fsXs, fontWeight: T.fwBold, color: T.projectsHubNavy, cursor: "pointer" }}>Retry</button>
        </div>
      )}
      {load.status === "loading" && byId && <div role="status" style={{ padding: "8px 14px", color: T.mid }}>Refreshing schedules…</div>}
      {load.status === "ok" && rows.length > 0 && rows.every(row => row.unscheduled) && (
        <div data-testid="projects-gantt-no-schedules" role="status" style={{ padding: "8px 14px", fontSize: T.fsXs, color: T.mid, borderBottom: "1px solid var(--v2-hair)" }}>
          No phase schedules recorded for these projects yet. Editors can add them from a project page.
        </div>
      )}
      <ScheduleTimeline
        rows={rows} scale={scale} today={today} portfolio density={density}
        placeholder={rows.length === 0 ? (
          <div data-testid="projects-gantt-empty" style={{ padding: 40, textAlign: "center", color: T.mid, fontSize: T.fsSm }}>No projects match your filters.</div>
        ) : load.status === "loading" && !byId ? (
          <div data-testid="projects-gantt-loading" aria-busy="true" style={{ padding: 14 }}>
            {rows.slice(0, 8).map((r, i) => (
              <div key={r.id} style={{ display: "flex", gap: 14, alignItems: "center", height: 34 }}>
                <div style={{ width: 200, height: 12, borderRadius: 6, background: T.lightGray }} />
                <div style={{ marginLeft: `${(i * 7) % 30}%`, width: `${20 + ((i * 13) % 25)}%`, height: 12, borderRadius: 6, background: T.lightGray }} />
              </div>
            ))}
          </div>
        ) : null}
      />
    </div>
  );
}
